/** Entity timeline card: merged email + meeting + note metadata, newest first, "Load 25 more"
 * via useInfiniteQuery (pages live in the query cache — no local accumulation state).
 * Emails open the message view (a `msg` selection — deep-linkable); notes open the note overlay
 * (the `note` selection param, OWNED HERE since the sibling Notes card was folded in — see the
 * header comment below); meetings render inline only.
 * A null email subject renders sealed-style — it may be a body_gated seal (not on the thread) or a
 * genuinely empty subject; the payload carries no distinction by design. Notes carry no subject —
 * an unreadable note is OMITTED entirely (Item E N4 — a preview IS body content), never sealed.
 *
 * Each row is two lines: a header (date · activity chip · title · open) and an "involved entities"
 * line (email From→To·Cc / meeting attendees / note author + tagged entities). The email chip shows
 * direction from EMBER's perspective (↑ Sent = an Ember person sent it, ↓ Received = they did not),
 * off the backend's `activity_type` tag — never the raw provenance column. Timeline v2 (E3): a compact
 * type-chip + "before"-date filter row, BACKEND-DRIVEN (useCrmActivityTypes → the shared activity
 * taxonomy), not a hardcoded label list (finding 12); the filter state is transient (useState).
 *
 * Pillar F.2 (owner ruling 2026-08-19): a mail or calendar row EXPANDS to reveal every participant,
 * fetched on expand from the governed per-item endpoint (`CrmParticipants`) — so a collapsed row costs
 * no request and the expansion carries the full, governed list rather than the summary's first few
 * names. External participants link to their profile and hovercard; Ember colleagues render as plain
 * text (the shipped internal-profile rule), and the server decides which is which. Notes do not
 * expand: they have no participants, and their content already opens in the note overlay.
 *
 * ⭐ SUPERSEDED IN PART by the timeline-inline ruling (2026-09-19,
 * `docs/specs/strategic-architecture/timeline-inline-message-spec.md`). What changed:
 *
 *  - **The collapsed line already IS the complete governed list.** The page payload carries every
 *    participant and every attendee WITH `profile_hidden` (spec §4), so the names link on the
 *    collapsed row and this surface no longer calls `/email/{id}/participants` or
 *    `/meeting/{id}/attendees` at all. One line, clipped, with Show more (`ParticipantLine`).
 *  - **Expanding an email row opens the MESSAGE BODY in place** — mounted on open, so a collapsed
 *    row still costs nothing (the F.2/F.5 structural rule, now applied to the body's own governed,
 *    audited door). Repeating the names under the row is what the ruling removed.
 *  - **Meeting rows no longer expand.** A meeting has no body, and every attendee is already linked
 *    on the collapsed line, so there is nothing left to reveal. The row is dated by the MEETING'S
 *    START (`occurred_at` = `crm_meeting.started_at`), never the invite's sent time.
 *  - **The email `open` button is gone**; the deep link lives on as "Full view ↗" inside the opened
 *    body, and `CrmMessageModal` is pending retirement for its remaining callers.
 *
 * ML-4 (manual-email-to-record-link-spec §3.1/§9): an email row the SERVER marks
 * `can_link_to_record` also carries a secondary **Link** affordance — de-emphasised until the row is
 * hovered or the button itself is focused, but a plain `<button>` in the head line at all times, so it
 * is keyboard reachable whether or not the row is expanded. The client never infers the flag: metadata
 * visibility is not authority to turn an email into deal evidence, and a row without the flag renders
 * nothing at all (no disabled control, no reason).
 *
 * ML-7 (same spec, §3.1/§3.3): a row the server says is ALREADY deal evidence gets a solid **Linked**
 * chip instead of that faint affordance — naming the deal(s) on hover or focus, navigating to the
 * record panel on click, and carrying a `+` that opens the same dialog so a second deal can be added
 * (links are additive; Link is never an implicit move). The governance is entirely the server's: a deal
 * this caller may not see is absent from `linked_records`, with no placeholder and no count, so the
 * chip has nothing to withhold. A row with no `linked_records` behaves exactly as it did before.
 *
 * The collapsed row stays TWO LINES and still reads only what the timeline payload already carries
 * (shipped with E3) — no per-row fetch. What changed with the timeline-inline ruling is that the
 * payload is now the COMPLETE governed list, with `profile_hidden` on it, so the second line is the
 * whole answer to "who was involved" rather than a summary of it, and expanding asks for the one
 * thing the page cannot carry: the message body.
 *
 * ⛔ THE ROW IS A GRID, NOT TWO STACKED FLEX LINES (timeline-row-layout-spec §2). Four tracks —
 * caret 14px · when 84px · centre `minmax(0, 1fr)` · actions `auto` — and three of the row's old
 * defects become properties of that grid rather than numbers anyone has to keep in step:
 *
 *  - the second line used to be a full-width sibling with a hand-tuned `padding-left: 94px`, so its
 *    names ran UNDER the Link / Linked column and stopped agreeing with the head line the moment the
 *    caret and the chip were added. The people line now lives in the centre cell, whose
 *    **`min-width: 0`** is the load-bearing property: it is what makes the subject ellipsize and
 *    `ParticipantLine` measure an overflow instead of the track simply growing.
 *  - the chip is the FIRST child of the centre cell, so its left edge IS the FROM label's left edge.
 *  - the controls no longer push themselves right with `margin-left: auto`; the `auto` track does it,
 *    which is why nothing can end up underneath them. The Linked popover is still absolutely
 *    positioned and may overhang the row — it is a popover, not row content.
 *
 * The `data-tl-*` attributes are this layout's test surface (the house pattern: `data-clamped`,
 * `data-participant-line`), because a CSS-module class name is not a contract a test can hold. */
import { useEffect, useMemo, useRef, useState } from 'react';

import { useSelectionParam } from '../../hooks/navigation/useUrlState';
import {
  crmEmailContentBase, useCrmActivityTypes, useCrmEmailBody, useCrmEmailContent,
  useCrmContentOpenKey, useCrmEmailBodyKey, useCrmEmailContentKey, useCrmTimeline, type CrmEntityKind,
} from '../../hooks/useCrm';
import { useCrmContentOnDemand } from '../../hooks/useCrmContentOnDemand';
import { navigateTab } from '../../hooks/useTabRouter';
import { EmailAttachments } from '../shared/EmailAttachments';
import { attachmentListStateFromQuery } from '../shared/attachmentDisplay';
import { InlineMessageBody } from '../shared/InlineMessageBody';
import { inlineMessageStateWithContent } from '../shared/inlineMessageState';
import { ParticipantLine, type ParticipantGroup } from '../shared/ParticipantLine';
import type { CrmTimelineLinkedRecord, CrmTimelineRow } from '../../types';
import { CrmLinkEmailModal } from './CrmLinkEmailModal';
import { CrmNoteOverlay } from './CrmNoteOverlay';
import type { OpenPerson } from './CrmParticipants';
import { msgHref } from './crmEntityRoles';
import { ensureUtc } from '../../utils/formatTimestamp';
import { fmtRowWhen } from './crmFormat';
import styles from './crm.module.css';

interface Props {
  kind: CrmEntityKind;
  id: number;
  /** Governed label already rendered by the host profile/panel. */
  hostLabel?: string | null;
  /** Navigate to the full, deep-linkable message PAGE (`?sel=msg:N`) — the "Full view ↗" anchor
   *  inside an opened body. The modal is not opened from this surface any more (spec §3.3).
   *  OPTIONAL: omitted, the anchor simply follows its own href (review F5); a no-op function
   *  would instead swallow the click and leave a dead control. */
  openMsgFullView?: (emailId: number) => void;
  /** In-place profile navigation for a linked participant. Omitted → names render without links. */
  openPerson?: OpenPerson;
}

const PAGE = 25;

/** The seal's explanation, which doubles as the `title` on a subject-less row. A null subject may be
 *  a body_gated seal or a genuinely empty subject and the payload carries no distinction — so the
 *  copy names both, and never claims a withholding it cannot verify. */
const SEALED_SUBJECT_TITLE =
  'Subject unavailable — empty, or hidden because you are not on this thread';

/** ONLY email rows expand, and what they expand to is the BODY (spec §3.2). A meeting has no body,
 *  and its attendees are already linked on the collapsed line — the caret would open nothing. */
function isExpandable(r: CrmTimelineRow): boolean {
  return r.kind === 'email';
}

/** Both sides through `ensureUtc` (spec §3). The wire's naive UTC would parse browser-local on each
 *  side, which happens to cancel out — until one of the two arrives with a `Z` and the other does
 *  not, and a note edited seconds after creation reads as edited hours before it. */
function isEdited(r: CrmTimelineRow): boolean {
  if (r.kind !== 'note' || !r.updated_at || !r.occurred_at) return false;
  return ensureUtc(r.updated_at).getTime() > ensureUtc(r.occurred_at).getTime();
}

/** Sent = the firm sent it. Reads ONLY `activity_type`, the backend's firm-perspective tag. It used
 *  to OR in the raw `direction` column, which is PROVENANCE (which mailbox folder the first sighting
 *  came from) — so mail Ember plainly sent showed "↓ Received" whenever a colleague's inbox was
 *  crawled before the sender's Sent Items. The raw column now arrives as `provenance_direction` and
 *  is deliberately not rendered (spec §10 decision 3: no divergence marker in S1). */
function isOutbound(r: CrmTimelineRow): boolean {
  return r.activity_type === 'email_sent';
}

/** The activity chip: kind + (for email) direction. Sent = an Ember person sent it; Received = they
 *  did not — the firm's perspective, never the mailbox folder the row happened to be created from. */
function ActivityChip({ r }: { r: CrmTimelineRow }) {
  if (r.kind === 'email') {
    const out = isOutbound(r);
    return (
      <span className={`${styles.chip} ${styles.kindEmail}`}>
        {out ? '↑ Sent' : '↓ Received'}
      </span>
    );
  }
  const cls = r.kind === 'meeting' ? styles.kindMeeting : styles.kindNote;
  return <span className={`${styles.chip} ${cls}`}>{r.kind === 'meeting' ? 'Meeting' : 'Note'}</span>;
}

/** The `when` cell: the date over the time, both from ONE normalised instant in ONE zone
 *  (`fmtRowWhen`, spec §3) — two formatters would let a row print "Sep 18" over "2:00 AM". The time
 *  span is omitted entirely when there is no timestamp, rather than rendering an empty line. */
function RowWhen({ value }: { value: string | null | undefined }) {
  const { date, time } = fmtRowWhen(value);
  return (
    <span className={styles.tlWhen} data-tl-when="">
      <span className={styles.tlWhenDate} data-tl-date="">{date}</span>
      {time !== '' && <span className={styles.tlWhenTime} data-tl-time="">{time}</span>}
    </span>
  );
}

function Party({ label, value }: { label: string; value: string }) {
  return (
    <span className={styles.tlParty}>
      <span className={styles.tlPartyLabel}>{label}</span>
      <span>{value}</span>
    </span>
  );
}

const MAIL_ROLES: Array<[string, string]> = [
  ['from', 'From'], ['to', 'To'], ['cc', 'Cc'],
  // bcc rows are server-redacted for everyone but the bcc'd principal and an audited all-access
  // read (governance.scoped_participants) — so if one arrives, it is meant for this caller.
  ['bcc', 'Bcc'],
];
const KNOWN_ROLES = new Set(MAIL_ROLES.map(([k]) => k));

/** The row's participants, banded for `ParticipantLine`.
 *
 * ⚠ `role` is nullable and NOT a closed enum on the wire, so banding on the four above alone DROPS
 * anything else — and this line is now the complete governed list rather than a summary, which is
 * exactly the F.2 "review minor 6" failure one surface up. Leftovers get their own band. */
function mailGroups(r: CrmTimelineRow): ParticipantGroup[] {
  const parts = r.participants ?? [];
  return [
    ...MAIL_ROLES.map(([key, label]) => ({ label, people: parts.filter((p) => p.role === key) })),
    { label: 'Other', people: parts.filter((p) => p.role == null || !KNOWN_ROLES.has(p.role)) },
  ];
}

/** Organizer first when the payload's `organizer_person_id` matches an attendee row, then the rest.
 *  Promoted rather than rendered twice off the bare id: the ATTENDEE row is the one carrying
 *  `profile_hidden`, so rendering the organizer from the id alone would lose the gate. */
function meetingGroups(r: CrmTimelineRow): ParticipantGroup[] {
  const attendees = r.attendees ?? [];
  const organizerId = r.organizer_person_id ?? null;
  const organizer = organizerId != null
    ? attendees.find((a) => a.resolved_person_id === organizerId) ?? null
    : null;
  const rest = organizer ? attendees.filter((a) => a !== organizer) : attendees;
  return [
    ...(organizer ? [{ label: 'Organizer', people: [organizer] }] : []),
    { label: 'Attendees', people: rest },
  ];
}

/** The "involved entities" line under each row. Mail and calendar rows render the COMPLETE governed
 *  list through the shared `ParticipantLine` — linked where the server allows it, one line, with a
 *  measured Show more. Note rows keep their author + tag chips. */
function InvolvedLine({ r, openPerson }: { r: CrmTimelineRow; openPerson?: OpenPerson }) {
  if (r.kind === 'email') {
    return <ParticipantLine groups={mailGroups(r)} openPerson={openPerson}
                            className={styles.tlParties} />;
  }
  if (r.kind === 'meeting') {
    if ((r.attendees ?? []).length > 0) {
      return <ParticipantLine groups={meetingGroups(r)} openPerson={openPerson}
                              className={styles.tlParties} />;
    }
    // The payload carried a count but no names — a meeting whose attendee rows the caller cannot
    // see named. Unchanged from E3.
    const n = r.attendee_count ?? 0;
    if (n > 0) {
      return <div className={styles.tlParties}>{`${n} attendee${n === 1 ? '' : 's'}`}</div>;
    }
    return null;
  }
  // note
  return (
    <div className={styles.tlParties}>
      <Party label="By" value={r.author_name ?? 'Someone'} />
      {r.links && r.links.length > 0 && (
        <span className={styles.tlTags}>
          {r.links.map((l) => (
            <span key={`${l.kind}-${l.id}`} className={styles.tlTag}>
              {l.name}
            </span>
          ))}
        </span>
      )}
    </div>
  );
}

/**
 * The opened row's MESSAGE BODY (spec §3.2) — the fetcher half, one per open row.
 *
 * ⛔ THE FETCH IS STRUCTURAL, NOT A FLAG. This component is MOUNTED when the row opens, so the hook
 * exists only then: a collapsed row has no query at all, which is what makes "one audited read per
 * open, never a prefetch" a property of the tree rather than a convention someone has to remember
 * (the F.2/F.5 rule, and spec §5.1). Collapse → expand inside React Query's 5-minute staleTime is
 * served from the cache, exactly as the modal is.
 *
 * ⚠ The caller keys it by email id. `ThreadedBody`'s "show earlier messages" state lives inside it,
 * and two rows open at once must not share one reader's "I have read this" (the F.3 review-minor-10
 * lesson, one surface over).
 */
function TimelineEmailBody({ emailId, openFullView }: {
  emailId: number;
  /** Optional: a host that cannot navigate to the message page gets a plain anchor that follows
   *  its own href, never a dead link (review F5). */
  openFullView?: (emailId: number) => void;
}) {
  const body = useCrmEmailBody(emailId, true);
  // EC-2: this surface's OWN governed status read, through its own audited door — shared rendering,
  // never a shared authorization. `enabled` is `true` only because this component mounts ONLY when
  // a row is expanded: a collapsed row, a hovercard and the page-level timeline fetch never reach
  // the content door (spec §3.1, the no-prefetch rule).
  const content = useCrmEmailContent(emailId, true);
  const contentState = attachmentListStateFromQuery(content);
  const statusKey = useCrmEmailContentKey(emailId);
  const openKey = useCrmContentOpenKey(`email:${emailId}`);
  // Memoised: a fresh array every render would re-run the loop's effects on every render.
  const bodyKey = useCrmEmailBodyKey(emailId);
  const bodyInvalidateKeys = useMemo(() => [bodyKey], [bodyKey]);
  // EC-5 — the expanded row's own acquisition loop. This component MOUNTS on expand, so a
  // collapsed row cannot reach it and cannot fire a POST (spec §3.1, the no-prefetch rule).
  const onDemand = useCrmContentOnDemand({
    base: crmEmailContentBase(emailId),
    openKey: openKey,
    content: contentState === 'ready' ? content.data : undefined,
    statusKey,
    enabled: true,
    // ⛔ **THE BODY QUERY IS RE-READ WHEN AN ACQUISITION LANDS** (Release B canary, msg 494083).
    // The status door and the body door are two different queries: without this the POST settles,
    // the envelope flips to `ready`, and the BODY query keeps serving the 404 it cached before the
    // fetch — so the surface still reads "No stored body for this message (metadata only)" for a
    // body that is sitting in the store. The key comes from the hook, never hand-built, so it
    // cannot drift from the one `useCrmEmailBody` actually reads.
    extraInvalidateKeys: bodyInvalidateKeys,
  });
  // ONE ladder, shared with the message page and the modal (review F3), widened by what the
  // envelope knows: a 404 on the byte door reads "Retrieving…" while an acquisition for this body
  // is in flight, and "No longer available" once a bounded search has completed (spec §3.2).
  const state = inlineMessageStateWithContent(
    body, contentState === 'ready' ? content.data?.body : undefined,
    { pending: onDemand.pending.has('body') });
  return (
    // ⛔ **ONE PLACED WRAPPER, AND EVERYTHING THE OPEN ROW SHOWS LIVES INSIDE IT.** The row is a
    // CSS grid (`.timelineRow2`, tracks `14px 84px minmax(0,1fr) auto`) and these used to be two
    // BARE SIBLINGS of it: the body carried `.tlBody`'s `grid-column: 3 / 5`, and the attachment
    // list carried no placement at all — so the grid auto-placed it into column 1, the 14px CARET
    // track. Its `auto` tracks (the type chip, the size, the buttons) overflowed and stayed
    // readable, while the filename's `minmax(0, 1fr)` collapsed to zero width and was clipped by
    // `overflow: hidden`. That is the Release B canary's "PDF · 2.9 MB · Retrieve with no
    // filename", on the surface where the full message view showed `Weil.pdf` from the very same
    // envelope. A wrapper is the fix rather than a second placement class because it makes the
    // failure unreachable: a future third child cannot be auto-placed into the caret track either.
    <div className={styles.tlOpen} data-timeline-open={emailId}>
    <InlineMessageBody
      className={styles.tlBody}
      state={state}
      text={body.data}
      header={(
        // A real anchor so ⌘-click / middle-click open a tab; a plain left click is intercepted
        // into the app's own selection navigation (never a hand-rolled pushState). This is the
        // deep-link path off this surface once the modal goes — nav spec §"a clickable item must
        // resolve a deep link".
        <a
          className={styles.tlFullView}
          href={msgHref(emailId)}
          onClick={(e) => {
            // ⛔ NO `preventDefault` UNLESS SOMETHING TAKES OVER (review F5) — and no in-place
            // navigation for a modified click. Mirrors `ParticipantName`: a handler that always
            // prevents the default turns the anchor into a dead control for any host that did not
            // pass an opener.
            if (openFullView == null || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
            e.preventDefault();
            openFullView(emailId);
          }}
        >
          Full view ↗
        </a>
      )}
    />
    {/* Below the body, never inside it: the body box is a capped, scrolled region and nothing
        governed or clickable lives inside a scrolled region but the body text (inline-message spec
        §5.4). A 403/404 status leaves this rendering nothing at all. */}
    <EmailAttachments
      emailId={emailId}
      // ⛔ ONE ladder, and it treats a refetch as LOADING. Handing the raw query's `data` down
      // painted the PREVIOUS open's filenames — with a live Download button — for as long as this
      // component stayed mounted after a revocation, because React Query keeps the last successful
      // data across a refetch and across a refetch that fails (review BLOCKER 1).
      state={contentState}
      content={contentState === 'ready' ? content.data : undefined}
      contentBase={crmEmailContentBase(emailId)}
      onDemand={onDemand}
    />
    </div>
  );
}

/** ML-7 — where a **Linked** chip's deal goes: the record panel over that deal's own list, in-tab.
 *  The SAME destination `CrmLinkedRecords` uses (`sel=list:<id>` + `rec=<entry>`), through the shared
 *  navigation helper — never a hand-rolled pushState, and never the Deals tab (owner ruling
 *  2026-08-21: a linked record points only at the IOT panel view of a deal). */
function openLinkedDeal(r: CrmTimelineLinkedRecord): void {
  navigateTab('crm', { params: { sel: `list:${r.list_id}`, rec: String(r.entry_id) } });
}

/** The accessible name of the chip. One deal names it; several state the count, because reading six
 *  deal names aloud before the row's subject is worse than "Linked to 6 deals" plus a list.
 *  Not exported: this file exports components only (react-refresh), and the copy is pinned by the
 *  RENDERED `aria-label` in `CrmTimeline.linked.test.tsx`, which is the string a reader hears. */
function linkedChipLabel(records: CrmTimelineLinkedRecord[]): string {
  return records.length === 1
    ? `Linked to ${records[0].name}`
    : `Linked to ${records.length} deals`;
}

/**
 * ML-7 — the solid **Linked** chip that replaces the faint **Link** affordance once an email is
 * already deal evidence (manual-email-to-record-link-spec §3.1/§3.3).
 *
 * The owner's case (2026-09-19): on Tecum Capital Partners the "Project Bluesky" thread is
 * scanner-bound to Charter Env and the "Charter partnership" thread is not — and the two rows were
 * indistinguishable, so the only way to find out was to open the link dialog and guess.
 *
 * - **The chip is full-opacity**, unlike the Link button it replaces: "this is already evidence" is a
 *   standing fact about the row, not an action that only matters on hover.
 * - **Hover OR keyboard focus** opens a small popover naming each deal as `name · stage`, with a
 *   `Proposed` badge where the payload says so. It is React state, not a CSS `:hover` rule, because a
 *   tooltip nothing can focus is invisible to a keyboard and untestable.
 * - **A single-deal chip navigates directly**; a multi-deal chip toggles the popover and each NAME in
 *   it navigates. Both go through `navigateTab`.
 * - **`+` is a separate control** (spec §3.4: links are ADDITIVE — one email can concern more than one
 *   matter, and linking never implies a move). It renders only when the server says
 *   `can_link_to_record`, exactly like the Link button: being able to SEE that an email is already
 *   evidence is not authority to make it evidence for something else.
 *
 * The component holds no governance logic. Every deal in `records` is one the server decided this
 * caller may open; a deal they may not see is absent from the payload, so there is nothing here to
 * hide and no count to render.
 */
function LinkedDealChip({ records, canLink, onLink }: {
  records: CrmTimelineLinkedRecord[];
  canLink: boolean;
  onLink: (invoker: HTMLButtonElement) => void;
}) {
  const [open, setOpen] = useState(false);
  const single = records.length === 1 ? records[0] : null;
  const label = linkedChipLabel(records);
  return (
    <span
      className={styles.tlLinkedWrap}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      // Focus/blur BUBBLE (unlike mouseenter/leave), so one pair on the wrapper covers the chip, the
      // `+` and every name inside the popover — which is what keeps the popover open while a keyboard
      // user tabs through the deals it lists.
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      <button
        type="button"
        className={styles.tlLinkedChip}
        title={label}
        aria-label={label}
        aria-expanded={single ? undefined : open}
        onClick={() => (single ? openLinkedDeal(single) : setOpen((v) => !v))}
      >
        Linked
      </button>
      {canLink && (
        <button
          type="button"
          className={styles.tlLinkedAdd}
          title="Link email to another deal"
          aria-label="Link email to another deal"
          onClick={(e) => onLink(e.currentTarget)}
        >
          +
        </button>
      )}
      {open && (
        <span className={styles.tlLinkedPop} role="group" aria-label={label}>
          {records.map((r) => (
            <button
              key={`${r.list_id}-${r.entry_id}`}
              type="button"
              className={styles.tlLinkedPopItem}
              onClick={() => openLinkedDeal(r)}
            >
              <span className={styles.tlLinkedPopName}>{r.name}</span>
              {r.stage && <span className={styles.tlLinkedPopStage}>{` · ${r.stage}`}</span>}
              {r.lifecycle_status === 'proposed' && (
                <span className={styles.tlLinkedProposed}>Proposed</span>
              )}
            </button>
          ))}
        </span>
      )}
    </span>
  );
}

export function CrmTimeline({ kind, id, hostLabel, openMsgFullView, openPerson }: Props) {
  const catalog = useCrmActivityTypes().data?.types ?? [];
  const [selected, setSelected] = useState<string[]>([]); // empty = All
  const [before, setBefore] = useState<string>(''); // yyyy-mm-dd or ''
  const filtered = selected.length > 0 || before !== '';
  // Which rows are open. Transient view state by the nav spec's rule (not a drill-down view), and a
  // LIST of row keys rather than a single id so several rows can be compared side by side.
  const [openRows, setOpenRows] = useState<string[]>([]);
  const toggleRow = (key: string) =>
    setOpenRows((s) => (s.includes(key) ? s.filter((k) => k !== key) : [...s, key]));

  const { value: noteParam, open: openNote, close: closeNote } = useSelectionParam('note');

  // ⛔ THE LINK DIALOG IS OWNED BY THE CARD, NOT THE ROW — see the CrmLinkEmailModal header. A
  // successful link invalidates this timeline, so the row that opened the dialog can be refetched
  // away in the same tick; a dialog mounted inside it would take its own confirmation with it.
  // Transient view state by the nav spec's modal rule (useState, never a URL param).
  const [linkFor, setLinkFor] = useState<
    {
      id: number; subject: string | null; occurredAt: string | null; threadId: string | null;
      /** `${kind}:${id}` at the moment Link was pressed — see the render guard below. */
      host: string;
    } | null
  >(null);
  // The Link button that opened the dialog, so focus goes back where it came from on close (spec
  // §3.2). Guarded with `document.contains`: a refetch may have unmounted the original row.
  const linkInvoker = useRef<HTMLButtonElement | null>(null);
  const closeLink = () => {
    setLinkFor(null);
    const el = linkInvoker.current;
    linkInvoker.current = null;
    if (el && document.contains(el)) el.focus();
  };
  // Same teardown reason as the note overlay below — a dialog that outlived a profile switch would
  // re-host onto the next entity and offer to link an email that is no longer on screen — but
  // DERIVED rather than an effect: the dialog renders only for the host it was opened on, so a
  // profile switch closes it in the same render instead of a cascading one.
  const hostKey = `${kind}:${id}`;

  // §7.6 teardown, inherited from the folded-in CrmNotesSection: drop the note param when the HOST
  // changes — NOT on mount, so a deep-linked note still opens. Without it the overlay survives a
  // profile switch (or a panel ‹ › hop) and re-hosts onto the next entity.
  const prevKey = useRef(`${kind}:${id}`);
  useEffect(() => {
    const key = `${kind}:${id}`;
    if (prevKey.current !== key) {
      prevKey.current = key;
      if (noteParam) closeNote();
      // …and the OPEN ROWS go with it (spec §3.2). Opening a row is an act, not a place, and the
      // same email appears on a person's profile and on their firm's — a keys-only state would
      // carry one profile's "I have read this message" onto the next entity's identical row.
      setOpenRows([]);
    }
  }, [kind, id, noteParam, closeNote]);
  const tl = useCrmTimeline(kind, id, PAGE, { types: selected, before: before || null });
  const rows = tl.data?.pages.flatMap((p) => p.rows) ?? [];

  const toggle = (key: string) =>
    setSelected((s) => (s.includes(key) ? s.filter((k) => k !== key) : [...s, key]));

  return (
    <div className={styles.card}>
      {/* `+ New note` is the ONLY note-creation entry point on a person/org profile now that the
          separate Notes card is gone, and it reuses the overlay the note rows already open: the
          `note` selection param with the 'new' sentinel, so it stays shareable, Back-closable and
          torn down with the host. Mirrors the record panel's own `+ Note` (E1b-2). */}
      <div className={styles.cardTitle}>
        Timeline
        <span className={styles.cardTitleNote}>emails + meetings + notes · entitled metadata</span>
        <button type="button" className={styles.cardTitleBtn} onClick={() => openNote('new')}>
          + New note
        </button>
      </div>

      <div className={styles.tlFilters}>
        <button
          type="button"
          className={`${styles.tlChip} ${selected.length === 0 ? styles.tlChipActive : ''}`}
          aria-pressed={selected.length === 0}
          onClick={() => setSelected([])}
        >
          All
        </button>
        {catalog.map((t) => {
          const on = selected.includes(t.key);
          return (
            <button
              key={t.key}
              type="button"
              className={`${styles.tlChip} ${on ? styles.tlChipActive : ''}`}
              aria-pressed={on}
              onClick={() => toggle(t.key)}
            >
              {t.label}
            </button>
          );
        })}
        <label className={styles.tlBefore}>
          Before
          <input type="date" value={before} onChange={(e) => setBefore(e.target.value)} />
        </label>
        {before !== '' && (
          <button type="button" className={styles.tlChipClear} onClick={() => setBefore('')}>
            clear date
          </button>
        )}
      </div>

      {tl.isLoading && <div className={styles.loading}>Loading timeline…</div>}
      {tl.error != null && rows.length === 0 && (
        <div className={styles.error}>Failed to load timeline.</div>
      )}
      {!tl.isLoading && !tl.error && rows.length === 0 && (
        <div className={styles.empty}>
          {filtered ? 'No activity matches this filter.' : 'No interactions recorded.'}
        </div>
      )}
      {rows.length > 0 && (
        <ul className={styles.timeline}>
          {rows.map((r) => {
            const rowKey = `${r.kind}-${r.id}`;
            const expandable = isExpandable(r);
            const isOpen = expandable && openRows.includes(rowKey);
            return (
              <li key={rowKey} className={styles.timelineRow2}>
                {expandable ? (
                  <button
                    type="button"
                    className={styles.tlCaret}
                    aria-expanded={isOpen}
                    aria-label={isOpen ? 'Hide message' : 'Show message'}
                    onClick={() => toggleRow(rowKey)}
                  >
                    {isOpen ? '▾' : '▸'}
                  </button>
                ) : (
                  <span className={styles.tlCaretSpacer} aria-hidden="true" />
                )}
                <RowWhen value={r.occurred_at} />
                {/* THE CENTRE CELL (spec §2). Chip first — its left edge is the FROM label's left
                    edge — then the subject, then the people line on the cell's second row. Its
                    `min-width: 0` is what clips both, so neither can widen the track or run under
                    the actions column. */}
                <div className={styles.tlCentre} data-tl-centre="">
                  <ActivityChip r={r} />
                  {r.kind === 'note' ? (
                    // ⛔ THE EDITED MARKER IS A SIBLING OF THE CLIPPED SPAN, never inside it — the
                    // same rule `ParticipantLine` follows for its Show more, and for the same
                    // reason: the preview now ellipsizes, so a marker within it is ellipsized AWAY
                    // on exactly the long notes most likely to have been edited. One flex line, the
                    // preview shrinking and the marker fixed.
                    <span className={styles.tlSubjectLine}>
                      <span className={styles.timelineSubject} title={r.preview || undefined}
                            data-tl-subject="">
                        {r.preview || '(empty note)'}
                      </span>
                      {isEdited(r) && (
                        <span className={styles.timelineEdited} data-tl-edited="">· edited</span>
                      )}
                    </span>
                  ) : expandable ? (
                    // The subject is the second expand target (the ruling is "clicking a row opens
                    // the message"); it stays a real button so it is keyboard-reachable.
                    // The `title` is now ALWAYS present: the subject is clipped to one line, so the
                    // full text has to be recoverable without opening the row. On a sealed subject
                    // it is still the seal's own explanation — one attribute, two jobs (spec §2).
                    <button
                      type="button"
                      className={`${styles.timelineSubjectBtn} ${r.subject == null ? styles.timelineSealed : ''}`}
                      aria-expanded={isOpen}
                      title={r.subject ?? SEALED_SUBJECT_TITLE}
                      onClick={() => toggleRow(rowKey)}
                    >
                      {r.subject ?? '(no subject)'}
                    </button>
                  ) : (
                    // A meeting: nothing to open, so the subject is plain text again rather than a
                    // control that does nothing (spec §3.2).
                    <span
                      className={`${styles.timelineSubject} ${r.subject == null ? styles.timelineSealed : ''}`}
                      title={r.subject ?? SEALED_SUBJECT_TITLE}
                    >
                      {r.subject ?? '(no subject)'}
                    </span>
                  )}
                  <InvolvedLine r={r} openPerson={openPerson} />
                </div>
                {/* THE ACTIONS CELL — its own track, so nothing on the row can sit beneath it. It
                    holds the row's ONE control and nothing else. */}
                <div className={styles.tlActions} data-tl-actions="">
                  {/* Rendered ONLY on the server's say-so. `can_link_to_record` is true when the
                      message is metadata-visible AND the governed content-axis body query authorizes
                      this principal — a subject the operator can read is not evidence they may bind
                      it. An unauthorized row gets no control and no explanation (spec §3.1). */}
                  {r.kind === 'email' && (r.linked_records?.length ?? 0) > 0 ? (
                    // ML-7: already evidence — say so, and let `+` add a SECOND deal (links are
                    // additive, never a move). The `+` carries the same `can_link_to_record` gate
                    // the Link button does: seeing that an email is evidence is not authority to
                    // make it evidence for something else.
                    <LinkedDealChip
                      records={r.linked_records as CrmTimelineLinkedRecord[]}
                      canLink={r.can_link_to_record === true}
                      onLink={(invoker) => {
                        linkInvoker.current = invoker;
                        setLinkFor({
                          id: r.id,
                          subject: r.subject ?? null,
                          occurredAt: r.occurred_at,
                          threadId: r.thread_id ?? null,
                          host: hostKey,
                        });
                      }}
                    />
                  ) : r.kind === 'email' && r.can_link_to_record === true ? (
                    <button
                      type="button"
                      className={styles.tlLinkBtn}
                      title="Link email to a deal"
                      aria-label="Link email to a deal"
                      onClick={(e) => {
                        linkInvoker.current = e.currentTarget;
                        setLinkFor({
                          id: r.id,
                          subject: r.subject ?? null,
                          occurredAt: r.occurred_at,
                          threadId: r.thread_id ?? null,
                          host: hostKey,
                        });
                      }}
                    >
                      Link
                    </button>
                  ) : null}
                  {r.kind === 'note' && (
                    <button className={styles.timelineOpen} onClick={() => openNote(String(r.id))}>
                      open
                    </button>
                  )}
                </div>
                {/* Mounted only while open — that is what makes "the body is read when the operator
                    opens the row, never for a collapsed one" structural rather than a convention.
                    KEYED by the email id so two open rows keep their own thread state. */}
                {isOpen && r.kind === 'email' && (
                  <TimelineEmailBody key={r.id} emailId={r.id} openFullView={openMsgFullView} />
                )}
              </li>
            );
          })}
        </ul>
      )}
      {tl.hasNextPage && (
        <button
          className={styles.loadMore}
          disabled={tl.isFetchingNextPage}
          onClick={() => tl.fetchNextPage()}
        >
          {tl.isFetchingNextPage ? 'Loading…' : `↓ Load ${PAGE} more`}
        </button>
      )}
      {tl.error != null && rows.length > 0 && (
        <div className={styles.error}>Failed to load more — try again.</div>
      )}
      {/* Mounted at the card ROOT, never inside a row: a save refetches the timeline, and an overlay
          living in the row it was opened from would unmount itself mid-edit. Same host + param
          contract the folded-in CrmNotesSection had. */}
      {/* ⛔ AT THE ROOT, for the reason in the state declaration above. */}
      {linkFor && linkFor.host === hostKey && (
        <CrmLinkEmailModal
          messageId={linkFor.id}
          subject={linkFor.subject}
          occurredAt={linkFor.occurredAt}
          threadId={linkFor.threadId}
          sourceKind={kind}
          sourceId={id}
          onClose={closeLink}
        />
      )}
      {noteParam && (
        <CrmNoteOverlay
          noteParam={noteParam}
          hostKind={kind}
          hostId={id}
          hostLabel={hostLabel}
          onClose={closeNote}
          onCreated={(nid) => openNote(String(nid), { replace: true })}
        />
      )}
    </div>
  );
}
